import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import CampaignIcon from '@mui/icons-material/Campaign'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import DashboardIcon from '@mui/icons-material/Dashboard'
import EventNoteIcon from '@mui/icons-material/EventNote'
import FolderSharedIcon from '@mui/icons-material/FolderShared'
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox'
import GroupsIcon from '@mui/icons-material/Groups'
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import PointOfSaleIcon from '@mui/icons-material/PointOfSale'
import QueryStatsIcon from '@mui/icons-material/QueryStats'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import SettingsIcon from '@mui/icons-material/Settings'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import TravelExploreIcon from '@mui/icons-material/TravelExplore'
import { isSanteSpaceEnabled } from '@/lib/Ameli/Core/healthCompanionFlag'
import { isConformityPauseAvailable } from '@/lib/Ameli/INSi/conformityPause'
import { getCareImpersonation } from '@/lib/auth/careImpersonation'
import { getSession } from '@/lib/auth/session'
import { isCareCampaignsEnabled } from '@/lib/care/campaign/careCampaignsFeatureFlag'
import { isCareExpertModeEnabled } from '@/lib/care/careExpertMode'
import { hasFullCareScope } from '@/lib/care/careScope'
import { routeFrontOfficeCommonUser } from '@/lib/routes/front/common/routeFrontOfficeCommonUser'
import { EntityList } from '@/types/Entity'
import { CONSTANTS } from '@/types/enum/constants'

// Compte comptoir (poste partage) : les ecrans d'administration de l'officine ne sont pas
// proposes au menu. Le menu seul est filtre : l'acces par URL reste celui du role.
const fnDisplayNotCounter = ({ dataMyProfile }: { dataMyProfile: any }) =>
  !dataMyProfile?.loggedUserRoles?.includes(CONSTANTS.ROLE_COMPTOIR)

// Flag serveur care_campaigns (dev/stage uniquement, OFF en prod) : masque l'entree de
// menu « Campagnes » et sa page d'edition. Les deux pages portent la meme garde, l'acces
// par URL directe ne passe pas par le menu.
const fnDisplayCareCampaigns = ({ dataMyProfile }: { dataMyProfile: any }) =>
  isCareCampaignsEnabled(dataMyProfile?.featureFlags)

// Titulaire ou co-titulaire delegue : ROLE_DYNAMIC_OWNER_PHARMACY couvre les deux.
// Passe par fnDisplay et NON par `roles`, que le .map() de fin de fichier ecrase pour
// toutes les routes du tableau. Le menu ne protege rien : la page et l'API refont le
// controle de leur cote.
const fnDisplayCareTeamActivity = ({ dataMyProfile }: { dataMyProfile: any }) =>
  !!(dataMyProfile?.loggedUserRoles || dataMyProfile?.roles)?.includes(
    CONSTANTS.ROLE_DYNAMIC_OWNER_PHARMACY
  )

// Connexion déléguée (même par un super admin) : les écrans centrés patient sortent du menu,
// l'API refuse leurs données. Le menu seul est filtré, la page affiche son propre encart.
const fnDisplayPatientData = () => !getCareImpersonation(getSession()).isImpersonating

export const getFrontOfficeCarePharmacien = [
  {
    route: 'Dashboard',
    text: 'Tableau de bord',
    t: 'routes.frontoffice.care.dashboard',
    icon: DashboardIcon,
    href: '/frontoffice/care/home',
    home: true,
    menuHeader: true,
    position: 10,
  },
  {
    route: 'CareAppointments',
    text: 'Gestion de mes rendez-vous',
    t: 'routes.frontoffice.care.appointments',
    icon: CalendarTodayIcon,
    href: '/frontoffice/care/appointments',
    menuHeader: true,
    position: 15,
  },
  {
    route: 'CarePlanning',
    text: 'Planning du personnel',
    t: 'routes.frontoffice.care.planning',
    icon: EventNoteIcon,
    href: '/frontoffice/care/planning',
    menuHeader: true,
    position: 16,
    // Fonctionnalite experte, encore en developpement : visible seulement si l'officine a
    // active le mode expert dans son parametrage. L'acces par URL directe reste ouvert.
    fnDisplay: ({ dataMyProfile }) =>
      fnDisplayNotCounter({ dataMyProfile }) && isCareExpertModeEnabled(dataMyProfile),
  },
  {
    route: 'Attendance',
    text: 'Fréquentation',
    t: 'routes.frontoffice.care.attendance',
    icon: QueryStatsIcon,
    href: '/frontoffice/care/attendance',
    menuHeader: true,
    position: 18,
  },
  {
    // Activité nominative de l'équipe : titulaire / co-titulaire délégué seulement,
    // même règle que le contrôle serveur (CareTeamActivityProvider).
    route: 'CareTeamActivity',
    text: "Activité de l'équipe",
    t: 'routes.frontoffice.care.teamActivity',
    icon: GroupsIcon,
    href: '/frontoffice/care/team-activity',
    menuHeader: true,
    position: 19,
    fnDisplay: fnDisplayCareTeamActivity,
  },
  {
    route: 'CareResources', // id conservé (D2-A) : appelé par les tours d'onboarding
    text: 'Bibliothèque',
    t: 'routes.frontoffice.care.library',
    icon: FolderSharedIcon,
    href: '/frontoffice/care/library',
    home: true,
    menuHeader: true,
    position: 20,
  },
  {
    route: 'CareBilling',
    text: 'Facturation et suivi',
    t: 'routes.frontoffice.care.billing',
    icon: ReceiptLongIcon,
    href: '/frontoffice/care/billing',
    menuHeader: true,
    position: 21,
    fnDisplay: ({ dataMyProfile }) =>
      fnDisplayNotCounter({ dataMyProfile }) && fnDisplayPatientData(),
  },
  {
    route: 'CareCommunication',
    text: 'Communication',
    t: 'routes.frontoffice.care.communication',
    icon: CampaignIcon,
    href: '/frontoffice/care/communication',
    menuHeader: true,
    position: 22,
  },
  {
    route: 'CareCampaigns',
    text: 'Campagnes',
    t: 'routes.frontoffice.care.campaigns',
    icon: ForwardToInboxIcon,
    href: '/frontoffice/care/campaigns',
    menuHeader: true,
    position: 23,
    fnDisplay: ({ dataMyProfile }) =>
      fnDisplayNotCounter({ dataMyProfile }) &&
      fnDisplayCareCampaigns({ dataMyProfile }) &&
      fnDisplayPatientData(),
  },
  {
    route: 'CareCredits',
    text: 'Crédits',
    t: 'routes.frontoffice.care.credits',
    icon: CreditCardIcon,
    href: '/frontoffice/care/credits',
    menuHeader: true,
    // Decale de 23 a 24 pour inserer Campagnes juste apres Communication (22).
    // La position ne sert qu'au tri du menu, aucun autre code ne la reference.
    position: 24,
    fnDisplay: fnDisplayNotCounter,
  },
  {
    route: 'CareTickets',
    text: 'Support',
    t: 'routes.frontoffice.care.tickets',
    icon: SupportAgentIcon,
    href: '/frontoffice/care/tickets',
    // Retirée du menu, la page reste accessible (détail de ticket, URL directe).
    menuHeader: false,
    position: 25,
  },
  {
    route: 'CareTicketDetail',
    text: 'Détail du ticket',
    t: 'routes.frontoffice.care.ticketDetail',
    href: '/frontoffice/care/tickets/[ticketId]',
    menuHeader: false,
    position: 26,
  },
  {
    route: 'CareFaq',
    text: 'FAQ',
    t: 'routes.frontoffice.care.faq',
    icon: HelpOutlineIcon,
    href: '/frontoffice/care/faq',
    menuHeader: false,
    position: 27,
  },
  {
    route: 'CareComptoir',
    text: 'Mon Comptoir',
    t: 'routes.frontoffice.care.comptoir',
    icon: PointOfSaleIcon,
    href: '/frontoffice/care/comptoir',
    menuHeader: true,
    position: 28,
    // Masqué sur le compte comptoir lui-même : gérer "Mon Comptoir" n'a de sens
    // que depuis un compte pharmacien classique. Masqué aussi pour un professionnel de
    // santé hors officine (kiné) : pas de compte comptoir, il travaille seul (D2,
    // docs/CARE_PROFESSIONS.md côté API). La page elle-même refait le contrôle.
    fnDisplay: ({ dataMyProfile }) =>
      fnDisplayNotCounter({ dataMyProfile }) &&
      fnDisplayPatientData() &&
      hasFullCareScope(dataMyProfile),
  },
  {
    // Espace sante (agrement SESAM-Vitale, carte n° 34) : en developpement, visible seulement si
    // NEXT_PUBLIC_SANTE_ENABLED=true (jamais en prod ni en staging). Care est ferme hors de France
    // par ENTITY_COUNTRIES. Visible aussi du compte comptoir : la session comptoir s'ouvre depuis
    // le poste partage. La page refait le controle de l'interrupteur.
    route: 'CareSante',
    text: 'Espace santé',
    t: 'routes.frontoffice.care.sante',
    icon: HealthAndSafetyIcon,
    href: '/frontoffice/care/sante',
    menuHeader: true,
    position: 29,
    fnDisplay: () => isSanteSpaceEnabled(),
  },
  // Ecrans du fichier patient, des ordonnances et des dispensations (carte n° 35, lot 14b) : hors
  // menu, ouverts depuis l'espace sante, sous le meme interrupteur (coupe en prod et staging).
  {
    route: 'CareSantePatients',
    text: 'Fichier patient',
    t: 'routes.frontoffice.care.santePatients',
    href: '/frontoffice/care/sante/patients',
    menuHeader: false,
    position: 29,
    fnDisplay: () => isSanteSpaceEnabled(),
  },
  {
    route: 'CareSantePatient',
    text: 'Fiche patient',
    t: 'routes.frontoffice.care.santePatient',
    href: '/frontoffice/care/sante/patients/[patientId]',
    menuHeader: false,
    position: 29,
    fnDisplay: () => isSanteSpaceEnabled(),
  },
  {
    route: 'CareSanteDispensation',
    text: 'Dispensation',
    t: 'routes.frontoffice.care.santeDispensation',
    href: '/frontoffice/care/sante/dispensations/[dispensationId]',
    menuHeader: false,
    position: 29,
    fnDisplay: () => isSanteSpaceEnabled(),
  },
  // Lots, ARL et suivi des factures (carte n° 36, lot 14c) : hors menu, meme interrupteur.
  {
    route: 'CareSanteBilling',
    text: 'Facturation santé',
    t: 'routes.frontoffice.care.santeBilling',
    href: '/frontoffice/care/sante/facturation',
    menuHeader: false,
    position: 29,
    fnDisplay: () => isSanteSpaceEnabled(),
  },
  {
    route: 'CareParametrage',
    text: 'Paramétrage',
    t: 'routes.frontoffice.care.parametrage',
    icon: SettingsIcon,
    href: '/frontoffice/care/parametrage',
    menuHeader: true,
    position: 30,
    fnDisplay: fnDisplayNotCounter,
  },
  {
    route: 'CareDossierList',
    text: 'Dossiers',
    t: 'routes.frontoffice.care.dossierList',
    href: '/frontoffice/care/dossier',
    menuHeader: false,
    position: 40,
  },
  {
    route: 'CareDossierDashboard',
    text: 'Dossier',
    t: 'routes.frontoffice.care.dossierDashboard',
    href: '/frontoffice/care/dossier/[dossierId]',
    menuHeader: false,
    position: 41,
  },
  {
    route: 'CareDossierEdit',
    text: 'Édition du dossier',
    t: 'routes.frontoffice.care.dossierEdit',
    href: '/frontoffice/care/dossier/[dossierId]/edit',
    menuHeader: false,
    position: 42,
  },
  {
    route: 'CareDossierConsent',
    text: 'Consentement',
    t: 'routes.frontoffice.care.dossierConsent',
    href: '/frontoffice/care/dossier/[dossierId]/consent',
    menuHeader: false,
    position: 43,
  },
  {
    route: 'CareDossierDmp',
    text: 'DMP',
    t: 'routes.frontoffice.care.dossierDmp',
    href: '/frontoffice/care/dossier/[dossierId]/dmp',
    menuHeader: false,
    position: 44,
  },
  {
    route: 'CareMission',
    text: 'Entretien',
    t: 'routes.frontoffice.care.mission',
    href: '/frontoffice/care/dossier/[dossierId]/entretien/[entretienId]',
    menuHeader: false,
    position: 44,
  },
  {
    route: 'CareMissionResume',
    text: 'Résumé entretien',
    t: 'routes.frontoffice.care.missionResume',
    href: '/frontoffice/care/dossier/[dossierId]/entretien/resume/[entretienId]',
    menuHeader: false,
    position: 45,
  },
  {
    route: 'CareBilanReperage',
    text: 'Repérage des risques individuels',
    t: 'routes.frontoffice.care.bilanReperage',
    href: '/frontoffice/care/dossier/[dossierId]/bilan-prevention/[missionId]/reperage',
    menuHeader: false,
    position: 46,
  },
  {
    route: 'CareBilanPlanPpp',
    text: 'Plan PPP',
    t: 'routes.frontoffice.care.bilanPlanPpp',
    href: '/frontoffice/care/dossier/[dossierId]/bilan-prevention/[missionId]/plan-ppp',
    menuHeader: false,
    position: 47,
  },
  {
    route: 'CareBilanResume',
    text: 'Résumé bilan',
    t: 'routes.frontoffice.care.bilanResume',
    href: '/frontoffice/care/dossier/[dossierId]/bilan-prevention/[missionId]/resume/[entretienId]',
    menuHeader: false,
    position: 48,
  },
  {
    route: 'CareBilanV2Construction',
    text: 'Bilan de prévention (V2)',
    t: 'routes.frontoffice.care.bilanV2Construction',
    href: '/frontoffice/care/dossier/[dossierId]/bilan-prevention/[missionId]/v2',
    menuHeader: false,
    position: 49,
  },
  // Parcours V2 du TROD angine, servi à toutes les officines (aiguillage dans
  // `lib/care/trodAngine/routing.ts`). Regroupé ici avec les autres parcours d'entretien plutôt
  // que trié par `position` : la route a `menuHeader: false`, la position ne pilote aucun affichage.
  {
    route: 'CareTrodAngineV2',
    text: 'TROD angine (V2)',
    t: 'routes.frontoffice.care.trodAngineV2',
    href: '/frontoffice/care/dossier/[dossierId]/trod-angine/[entretienId]/v2',
    menuHeader: false,
    position: 51,
  },
  {
    // Déroulé d'une séance du sevrage tabagique (repérage, séances 1 à 3). Hors menu : on y
    // entre depuis la fiche patient (`lib/care/missionNavigation.ts`).
    route: 'CareSevrageTabagique',
    text: 'Sevrage tabagique',
    t: 'routes.frontoffice.care.sevrageTabagique',
    href: '/frontoffice/care/dossier/[dossierId]/sevrage-tabagique/[entretienId]',
    menuHeader: false,
    position: 51,
  },
  {
    // Suivi du patient dans la mission sevrage tabagique (frise des séances, prochain rendez-vous,
    // plan en cours). Hors menu : on y entre depuis la fiche patient.
    route: 'CareSevrageTabagiqueSuivi',
    text: 'Sevrage tabagique',
    t: 'routes.frontoffice.care.sevrageTabagique',
    href: '/frontoffice/care/dossier/[dossierId]/sevrage-tabagique/suivi/[missionId]',
    menuHeader: false,
    position: 51,
  },
  {
    // Parcours vaccination V2 (mission vaccination, étape 19). Aiguillage V1 / V2 dans
    // `lib/care/vaccinationVersion.ts`. Hors menu, pas d'espace INDUSTRY, pas de test pays :
    // Care est déjà limité à la France par l'entité PHARMEDICARE.
    route: 'CareVaccinationV2',
    text: 'Vaccination (V2)',
    t: 'routes.frontoffice.care.vaccinationV2',
    href: '/frontoffice/care/dossier/[dossierId]/vaccination/[entretienId]/v2',
    menuHeader: false,
    position: 51,
  },
  {
    route: 'CareCampaignEdit',
    text: 'Campagne',
    t: 'routes.frontoffice.care.campaignEdit',
    href: '/frontoffice/care/campaigns/[campaignId]',
    menuHeader: false,
    position: 50,
    fnDisplay: fnDisplayCareCampaigns,
  },
  ...routeFrontOfficeCommonUser,
].map(route => ({
  ...route,
  entity: [EntityList.PHARMEDICARE],
  roles: [CONSTANTS.ROLE_DYNAMIC_ROUTE_CARE_MANAGE],
}))

// Care NON ACTIVE : l'entree de menu EST l'action d'activation (modele « Activer ma
// radio »). Reservee au titulaire/co-titulaire. La cible est la home group avec
// ?activateCare=1 : la modale d'activation de prod (Neo) s'y ouvre — la meme que celle
// de l'etape « Activer mes missions » du parcours.
// Le « France uniquement » vient de ENTITY_COUNTRIES[PHARMEDICARE] et s'applique tout
// seul via l'entite de la route : ne PAS le redire ici (cf. lib/country/entityCountries).
export const getFrontOfficeCareActivation = [
  {
    // Traces SOAP Ameli — DEV UNIQUEMENT. Exigence du package CNDA INSi (§ 1.1) : pendant la
    // verification de conformite, « le logiciel devra permettre l'acces aux traces xml des
    // requetes emises et des reponses recues ». Meme garde que la pause de conformite : ces
    // outils de campagne vivent et meurent ensemble. L'API refuse de son cote, l'acces par URL
    // directe ne passe donc pas davantage.
    route: 'CareAmeliTraces',
    text: 'Traces SOAP Ameli (dev)',
    t: 'routes.frontoffice.care.ameliTraces',
    icon: TravelExploreIcon,
    href: '/frontoffice/care/ameli-traces',
    menuHeader: true,
    position: 99,
    fnDisplay: () => isConformityPauseAvailable(),
    // `entity` et `roles` sont OBLIGATOIRES ici : ce tableau n'a pas de `.map()` final, chaque
    // route porte donc les siens. Sans `entity`, `getListRouteAvailable()` casse sur
    // `route.entity.includes(...)` et fait tomber TOUT le back-office — pas seulement cette
    // route : la derivation d'entites echoue, et avec elle chaque cellule qui construit un lien
    // via getRoutes (colonnes « Nom » et « CIP » vides sur les listes).
    //
    // `fnDisplay` ne remplace pas `entity` : la derivation d'entites par routes l'IGNORE
    // (cf. .claude/rules/country-scope-front.md). La garde « dev uniquement » reste portee par
    // `fnDisplay`, le perimetre par `entity`.
    entity: [EntityList.PHARMEDICARE],
    roles: [CONSTANTS.ROLE_DYNAMIC_OWNER_PHARMACY, CONSTANTS.ROLE_COTITULAIRE_PHARMACIEN],
  },
  {
    route: 'CareActivate',
    text: 'Activer mes missions',
    t: 'routes.frontoffice.care.activate',
    icon: DashboardIcon,
    href: '/frontoffice/group/home',
    queryParams: { activateCare: '1' },
    menuHeader: true,
    position: 10,
    fnDisplay: ({ dataMyProfile }) => !dataMyProfile?.selectCompanyDefault?.companyCare?.activeCare,
    entity: [EntityList.PHARMEDICARE],
    roles: [CONSTANTS.ROLE_DYNAMIC_OWNER_PHARMACY, CONSTANTS.ROLE_COTITULAIRE_PHARMACIEN],
  },
  {
    // Meme modele que CareActivate ci-dessus, pour le professionnel de sante hors officine
    // (kine bilan de prevention, cf. .claude/rules/ + docs/CARE_PROFESSIONS.md cote API).
    // Cible dediee : `/frontoffice/group/home` est gardee par ROLE_DYNAMIC_ROUTE_GROUP_PHARMACIEN,
    // que ce profil n'a jamais (il n'a que School + Insight a l'inscription). La page monte la
    // meme modale d'activation (Neo), adaptee au type de structure.
    route: 'CareActivateHealthProfessional',
    text: 'Activer mes missions',
    t: 'routes.frontoffice.care.activate',
    icon: DashboardIcon,
    href: '/frontoffice/care/activate',
    menuHeader: true,
    position: 10,
    fnDisplay: ({ dataMyProfile }) => !dataMyProfile?.selectCompanyDefault?.companyCare?.activeCare,
    entity: [EntityList.PHARMEDICARE],
    roles: [CONSTANTS.ROLE_DYNAMIC_OWNER_CARE_HEALTH_PROFESSIONAL],
  },
]
